import { useEffect, useRef } from 'react';
import type { Action, History, Location } from 'history';

import { useHistory } from './useHistory';

export type HistoryBlockHookOptions = {
    isEnabled?: boolean | (() => boolean);
    block: (transition: HistoryBlockHookTransition) => Unblock | void;
};

export type HistoryBlockHookTransition = {
    action: Action;
    location: Location;
    unblock: Unblock;
};

type Unblock = () => void;

export function useHistoryBlock({ block, isEnabled = true }: HistoryBlockHookOptions) {
    const history = useHistory();
    const blockRef = useRef(block);
    const isEnabledRef = useRef(isEnabled);

    /**
     * При изменении функции block не требуется переустанавливать слушателей,
     * поэтому можно всегда использовать последнюю версию,
     * не указывая её в deps внутри useEffect.
     */
    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    blockRef.current = block;

    useEffect(() => {
        isEnabledRef.current = isEnabled;

        if (!isEnabled) {
            return;
        }

        let uninstall: () => void;
        let unlisten: (() => void) | undefined;
        let unblock: (() => void) | undefined;

        install();

        function install() {
            uninstall = history.block(
                ({ action, location, retry }) => {
                    /**
                     * В случае если блокировка задается функцией, сразу проверяем отсутствие блокировки.
                     */
                    if (typeof isEnabled === 'function' && !isEnabled()) {
                        // Удалить блокировщик для совершения перехода
                        uninstall?.();

                        /**
                         * После первого перехода нужно снова установить блокировщик.
                         * При переходе может не произойти рендер компонента,
                         * использующего хук, поэтому нужно отслеживать переход через
                         * механизм подписки.
                         */
                        unlisten = listenHistoryOnce(history, () => {
                            install();
                        });

                        // Повторить переход без блокировщика
                        retry();

                        return;
                    }

                    unblock =
                        blockRef.current({
                            action,
                            location,
                            unblock() {
                                /**
                                 * Чтобы совершить переход, нужно удалить блокировщик,
                                 * иначе вызов retry будет заблокирован.
                                 */
                                uninstall();
                                unblock?.();

                                // Обработка возможного отсутствия ререндера хука, см. выше
                                if (isEnabledRef.current) {
                                    unlisten = listenHistoryOnce(history, () => {
                                        install();
                                    });
                                }

                                // Повторить переход без блокировщика
                                retry();
                            },
                        }) ?? undefined;
                },
                // Функция для beforeunload
                typeof isEnabled === 'function' ? isEnabled : () => isEnabled
            );
        }

        return () => {
            uninstall();
            unblock?.();
            unlisten?.();
        };
    }, [isEnabled, history]);
}

/**
 * Отписка в history.listen может сработать не мгновенно,
 * если переход был выполнен внутри другого listen.
 *
 * ```ts
 * history.listen(({ location }) => {
 *     if (location.pathname === '/path') {
 *         history.replace('/replaced-path');
 *     }
 * });
 * const unlisten = history.listen(() => {
 *     unlisten();
 *     // вызовется два раза, несмотря на немедленный вызов unlisten():
 *     // - первый вызов: "REPLACE /replaced-path"
 *     // - второй вызов: "PUSH /path"
 * });
 *
 * history.push('/path');
 * ```
 *
 * @param history - объект для манипуляции историей
 * @param callback - будет вызван единожды при изменении истории
 *
 * @returns функция отписки
 */
function listenHistoryOnce(history: History, callback: Parameters<History['listen']>[0]) {
    let callbackHaveBeenCalled = false;

    const unlistenHistory = history.listen((...args) => {
        if (!callbackHaveBeenCalled) {
            callback(...args);
        }

        unlisten();
    });

    function unlisten() {
        callbackHaveBeenCalled = true;
        unlistenHistory();
    }

    return unlisten;
}
